<script setup lang="ts">
import {ref} from 'vue';
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Clock3,
  FileText,
  HeartHandshake,
  Send,
  Sparkles,
  Upload,
  Users,
  X
} from 'lucide-vue-next';
import '@/assets/styles/career.css'
import '@/assets/styles/upload-fix.css'
import '@/assets/styles/jobs-fix.css'

const open = ref(false), sending = ref(false), success = ref(false), error = ref('');
const resume = ref<File | null>(null), cover = ref<File | null>(null)
const form = ref({
  position: 'Festanstellung im Praxisteam',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  workModel: 'Vollzeit',
  message: '',
  privacy: false
})

function openApplication(position: string, workModel: string) {
  form.value.position = position;
  form.value.workModel = workModel;
  open.value = true;
}

function pick(e: Event, type: 'resume' | 'cover') {
  const file = (e.target as HTMLInputElement).files?.[0] || null;
  if (type === 'resume') resume.value = file; else cover.value = file
}

async function submit() {
  if (!resume.value) {
    error.value = 'Bitte laden Sie Ihren Lebenslauf als PDF hoch.';
    return;
  }
  sending.value = true;
  error.value = '';
  await new Promise(resolve => setTimeout(resolve, 800));
  success.value = true;
  sending.value = false
}

function close() {
  open.value = false;
  success.value = false;
  error.value = ''
}
</script>
<template>
  <div>
    <section class="page-hero career-hero">
      <div class="container"><span class="eyebrow">Karriere in unserer Praxis</span>
        <h1>Arbeiten, wo der Mensch<br><em>im Mittelpunkt steht.</em></h1>
        <p>Ein herzliches Team, moderne Zahnmedizin und ein Arbeitsplatz, an dem Professionalität und gutes Miteinander
          zusammengehören.</p>
        <div class="career-tags"><span><Check/> Vollzeit möglich</span><span><Check/> Teilzeit möglich</span><span><Check/> Moderne Praxis</span>
        </div>
      </div>
    </section>
    <section class="section">
      <div class="container">
        <div class="section-heading">
          <div><span class="eyebrow">Das erwartet Sie</span>
            <h2>Ein Team, in dem man<br>gerne ankommt.</h2></div>
          <p>Wir arbeiten strukturiert, unterstützen einander und legen Wert auf eine offene, freundliche Atmosphäre –
            im Team genauso wie im Umgang mit unseren Patientinnen und Patienten.</p></div>
        <div class="home-features">
          <div>
            <HeartHandshake/>
            <h3>Starkes Miteinander</h3>
            <p>Kurze Wege, gegenseitige Unterstützung und ein respektvoller Umgang.</p></div>
          <div>
            <Sparkles/>
            <h3>Moderner Arbeitsplatz</h3>
            <p>Ein hochwertiges Praxisumfeld mit anspruchsvoller Zahnmedizin.</p></div>
          <div>
            <Clock3/>
            <h3>Passendes Arbeitsmodell</h3>
            <p>Die aktuelle Stelle ist sowohl in Vollzeit als auch in Teilzeit möglich.</p></div>
        </div>
      </div>
    </section>
    <section class="section jobs-section">
      <div class="container">
        <div class="jobs-heading"><span class="eyebrow">Offene Stellen</span>
          <h2>Vielleicht passen wir zusammen.</h2>
          <p>Für den ersten Schritt reichen wenige Angaben. Ihre Unterlagen können Sie direkt hochladen.</p></div>
        <article class="job-list-card">
          <div class="job-main">
            <div class="job-status">Aktuell gesucht</div>
            <h3>Verstärkung für unser Praxisteam <small>(m/w/d)</small></h3>
            <p>Sie sind zuverlässig, arbeiten gerne mit Menschen und möchten Ihre Stärken in einer modernen
              Zahnarztpraxis einbringen? Wir freuen uns auf Ihre Bewerbung.</p>
            <ul>
              <li>
                <Check/>
                Freude am Umgang mit Menschen
              </li>
              <li>
                <Check/>
                Zuverlässige und sorgfältige Arbeitsweise
              </li>
              <li>
                <Check/>
                Teamgeist und freundliches Auftreten
              </li>
            </ul>
          </div>
          <div class="job-action">
            <div class="job-pills"><span><Clock3/> Vollzeit</span><span><Users/> Musterstadt</span>
            </div>
            <button class="button primary" @click="openApplication('Festanstellung in Vollzeit','Vollzeit')">Jetzt bewerben
              <ArrowRight/>
            </button>
          </div>
        </article>
        <article class="job-list-card parttime-card">
          <div class="job-main">
            <div class="job-status parttime-status">Teilzeitstelle</div>
            <h3>Verstärkung in Teilzeit <small>(m/w/d)</small></h3>
            <p>Sie möchten Beruf und Privatleben gut miteinander verbinden und trotzdem Teil eines engagierten Praxisteams sein? Wir suchen zuverlässige Unterstützung in Teilzeit.</p>
            <ul>
              <li><Check/> Flexible Teilzeitbeschäftigung</li>
              <li><Check/> Freude am Kontakt mit Patientinnen und Patienten</li>
              <li><Check/> Sorgfältige, selbstständige Arbeitsweise</li>
            </ul>
          </div>
          <div class="job-action">
            <div class="job-pills"><span><Clock3/> Teilzeit</span><span><Users/> Musterstadt</span></div>
            <button class="button primary" @click="openApplication('Teilzeitstelle im Praxisteam','Teilzeit')">In Teilzeit bewerben <ArrowRight/></button>
          </div>
        </article>
        <article class="job-list-card training-card">
          <div class="job-main">
            <div class="job-status training-status">Ausbildungsplatz</div>
            <h3>Ausbildung in unserer Zahnarztpraxis <small>(m/w/d)</small></h3>
            <p>Du möchtest in einen abwechslungsreichen medizinischen Beruf starten, arbeitest gerne mit Menschen und suchst ein Team, das dich zuverlässig begleitet? Dann lerne unsere Praxis kennen.</p>
            <ul>
              <li><Check/> Interesse an Medizin und Zahnheilkunde</li>
              <li><Check/> Freundlichkeit und Freude am Umgang mit Menschen</li>
              <li><Check/> Lernbereitschaft, Sorgfalt und Teamgeist</li>
            </ul>
          </div>
          <div class="job-action">
            <div class="job-pills"><span><Sparkles/> Ausbildung</span><span><Users/> Musterstadt</span></div>
            <button class="button primary" @click="openApplication('Ausbildung in der Zahnarztpraxis','Ausbildung')">Ausbildung starten <ArrowRight/></button>
          </div>
        </article>
      </div>
    </section>
    <transition name="modal">
      <div v-if="open" class="application-overlay" @click.self="close">
        <div class="application-modal">
          <button class="modal-close" aria-label="Schließen" @click="close">
            <X/>
          </button>
          <div v-if="success" class="application-success">
            <CheckCircle2/>
            <h2>Vielen Dank!</h2>
            <p>Ihre Bewerbung wurde erfolgreich übermittelt. Wir melden uns persönlich bei Ihnen.</p>
            <button class="button primary" @click="close">Schließen</button>
          </div>
          <template v-else>
            <div class="modal-heading"><span class="eyebrow">Direkt bewerben</span>
              <h2>Bewerbung: {{ form.position }}</h2>
              <p>Füllen Sie das Formular aus und laden Sie Ihre Unterlagen bequem als PDF hoch.</p></div>
            <form class="contact-form application-form" @submit.prevent="submit">
              <div class="field-row"><label><span>Vorname *</span><input v-model="form.firstName" required
                                                                         placeholder="Max"></label><label><span>Nachname *</span><input
                  v-model="form.lastName" required placeholder="Mustermann"></label></div>
              <div class="field-row"><label><span>E-Mail *</span><input v-model="form.email" required type="email"
                                                                        placeholder="mail@beispiel.de"></label><label><span>Telefon</span><input
                  v-model="form.phone" type="tel" placeholder="+49 …"></label></div>
              <label><span>Gewünschtes Arbeitsmodell</span><select v-model="form.workModel">
                <option>Vollzeit</option>
                <option>Teilzeit</option>
                <option>Offen für beides</option>
                <option>Ausbildung</option>
              </select></label><label><span>Kurze Nachricht *</span><textarea v-model="form.message" required rows="4"
                                                                              placeholder="Erzählen Sie uns kurz etwas über sich …"></textarea></label>
              <div class="upload-grid"><label class="upload-card"><input required type="file" accept="application/pdf,.pdf"
                                                                         @change="pick($event,'resume')">
                <Upload/>
                <span><strong>Lebenslauf hochladen *</strong><small>{{
                    resume ? resume.name : 'PDF auswählen'
                  }}</small></span></label><label
                  class="upload-card"><input type="file" accept="application/pdf,.pdf" @change="pick($event,'cover')">
                <FileText/>
                <span><strong>Anschreiben hochladen</strong><small>{{
                    cover ? cover.name : 'PDF auswählen'
                  }}</small></span></label>
              </div>
              <label class="privacy-check"><input v-model="form.privacy" required type="checkbox"><span>Ich stimme der Verarbeitung meiner Bewerbungsdaten gemäß <router-link
                  to="/datenschutz">Datenschutzerklärung</router-link> zu.</span></label>
              <p v-if="error" class="form-error">{{ error }}</p>
              <button class="button primary submit-application" :disabled="sending">
                <Send/>
                {{ sending ? 'Wird übermittelt …' : 'Bewerbung senden' }}
              </button>
            </form>
          </template>
        </div>
      </div>
    </transition>
  </div>
</template>
